import { VueI18n } from "vue-i18n";
import { ApiErrorResponse } from "@/stores/errors/ApiErrorMessageBag.ts";

/**
 * Represents validation errors for a specific form field.
 *
 * This class encapsulates one or more validation errors associated with a single form field.
 * It provides methods to convert the errors to localized or non-localized string representations,
 * making it suitable for displaying field-specific error messages in forms.
 *
 * The class handles complex scenarios including:
 * - Multiple validation errors per field
 * - Field name translation and mapping
 * - Dynamic parameter interpolation in error messages
 * - Fallback behavior for missing translations
 *
 * @example
 * ```typescript
 * const emailError = new FieldValidationError('email', [
 *   { key: 'validation.required', message: 'Email is required', parameters: {} },
 *   { key: 'validation.email', message: 'Must be a valid email', parameters: {} }
 * ]);
 *
 * // Display localized error
 * console.log(emailError.toLocaleString(i18n)); // "Email is required, Must be a valid email"
 *
 * // Display raw error
 * console.log(emailError.toString()); // "Email is required, Must be a valid email"
 * ```
 */
export class FieldValidationError {

	/**
	 * Creates a new FieldValidationError instance.
	 *
	 * @param fieldName - The name of the form field this error relates to.
	 *                    Supports dot notation for nested fields (e.g., 'address.street').
	 * @param errorObjects - An array of error response objects containing the validation errors.
	 *                       Each object includes a key for i18n lookup, a fallback message,
	 *                       and optional parameters for message interpolation.
	 */
	constructor(
		private fieldName: string,
		private errorObjects: ApiErrorResponse[],
	) {

	}

	/**
	 * Converts all validation errors to a localized, comma-separated string.
	 *
	 * This method iterates through all error objects and translates each one
	 * using the provided i18n instance. The translated messages are then
	 * joined with commas.
	 *
	 * @param i18n - Optional Vue I18n instance for translation lookup.
	 *               If not provided, falls back to raw error messages.
	 * @returns A comma-separated string of all localized error messages.
	 *
	 * @example
	 * ```typescript
	 * const localizedError = fieldError.toLocaleString(i18n);
	 * // Output: "Name is required, Name must be at least 2 characters"
	 * ```
	 */
	toLocaleString(i18n?: VueI18n): string {
		return this.errorObjects.map(
			(errorData) => this.getErrorMessage(errorData, i18n)
		).join(', ');
	};

	/**
	 * Converts all validation errors to a non-localized, comma-separated string.
	 *
	 * This method returns the raw error messages from the API without any translation,
	 * making it useful for logging or when i18n is not available.
	 *
	 * @returns A comma-separated string of all raw error messages.
	 *
	 * @example
	 * ```typescript
	 * console.log(fieldError.toString());
	 * // Output: "The name field is required., The name must be at least 2 characters."
	 * ```
	 */
	toString(): string {
		return this.errorObjects.map(
			(errorData) => errorData.message
		).join(', ');
	};

	/**
	 * Maps API field names to their corresponding i18n translation keys.
	 *
	 * This method provides a hook for customizing how field names are translated
	 * in error messages. It's useful when:
	 * - The API field name differs from the UI label (e.g., 'birthdate' → 'Date of birth')
	 * - A more user-friendly field name is needed in error messages
	 * - Field names need domain-specific translation
	 *
	 * @param fieldName - The API field name to map.
	 * @returns The mapped i18n key string, or `null` if no mapping exists.
	 *          When `null` is returned, the original field name is used with
	 *          automatic capitalization logic.
	 * @private
	 *
	 * @example
	 * ```typescript
	 * // Add custom mappings in this method:
	 * switch (fieldName) {
	 *   case 'dob':
	 *     return 'Date of birth';
	 *   case 'pwd':
	 *     return 'Password';
	 *   default:
	 *     return null;
	 * }
	 * ```
	 */
	private mapFieldNameToI18nKey(fieldName: string): string | null {
		switch (fieldName) {
			case 'birthdate':
				return 'Date of birth';

			default:
				return null;
		}
	}

	/**
	 * Generates a localized error message for a single validation error.
	 *
	 * This method performs several steps to produce the final error message:
	 *
	 * 1. **Message key mapping**: Maps API error keys (e.g., 'validation.required')
	 *    to i18n translation keys (e.g., 'field required').
	 *
	 * 2. **Field name resolution**: Determines the display name for the field by:
	 *    - First checking for a custom mapping via `mapFieldNameToI18nKey()`
	 *    - Then attempting to translate the raw field name
	 *    - Finally falling back to capitalized field name
	 *
	 * 3. **Parameter interpolation**: Combines API-provided parameters with
	 *    the resolved field name for message interpolation.
	 *
	 * @param errorObject - The error response object containing the error details.
	 * @param i18n - Optional Vue I18n instance for translation. If not provided,
	 *               returns the raw error message from the API.
	 * @returns The fully localized and interpolated error message string.
	 * @private
	 *
	 * @example
	 * ```typescript
	 * // Input: { key: 'validation.max.string', message: '...', parameters: { max: '255' } }
	 * // Output: "Name must be at most 255 characters"
	 * ```
	 */
	private getErrorMessage(errorObject: ApiErrorResponse, i18n?: VueI18n)
	{
		// No language provided? Use the message as provided by the API
		if (!i18n) {
			return errorObject.message;
		}

		let message = '';
		switch(errorObject.key) {
			case "validation.required":
				message = "field required";
				break;
			case "validation.max.string":
				message = "field maximum characters";
				break;
			case "validation.mimes":
				message = "file type not supported";
				break;
			default:
				message = errorObject.message;
				break;
		}

		let fieldNameParameter;
		if (this.fieldName) {

			// First, attempt to map the field name to a specific i18n key
			const mappedFieldName = this.mapFieldNameToI18nKey(this.fieldName);
			if (mappedFieldName && i18n.te(mappedFieldName)) {
				fieldNameParameter = {
					fieldName: i18n.t(mappedFieldName)
				};
			}

			// Secondly, attempt to translate the field name as-is
			else if (i18n.te(this.fieldName)) {
				fieldNameParameter = {
					fieldName: i18n.t(this.fieldName)
				};

			// Else, try to capitalize the field name and do not translate
			} else {

				let capitalizeFieldName;
				capitalizeFieldName = this.fieldName.charAt(0).toUpperCase() + this.fieldName.slice(1);
				//remove "s", if capitalizeFieldName doesn't exist in the translation file, to make the noun singular, so it can be translated
				if (capitalizeFieldName.endsWith("s") && !i18n.te(capitalizeFieldName)) {
					capitalizeFieldName = capitalizeFieldName.slice(0, -1);
				}

				fieldNameParameter = {fieldName: capitalizeFieldName};

				// Attempt to translate
				if (i18n.te(capitalizeFieldName)) {
					fieldNameParameter = {
						fieldName: i18n.t(capitalizeFieldName)
					};
				}

			}
		} else {
			// pretty sure this never happens
			fieldNameParameter = {fieldName: '[fieldName]'};
		}


		const combinedDynamicParameters = Object.assign({}, errorObject.parameters, fieldNameParameter);
		return i18n.t('#error: ' + message, combinedDynamicParameters);
	}



}
